// Builder comparison data for BuildPilot partner builders (SA + VIC).
// This data is used for neutral, informational side-by-side comparisons.
//
// Each builder carries an explicit `states` array (e.g. ['SA'], ['VIC'],
// or ['SA','VIC'] for cross-state partners). SA-facing surfaces MUST
// filter by state via `getBuildersByState('SA')` so VIC-only partners
// (like Burra Built) don't leak into Adelaide contexts, and vice versa.
// Do NOT rely on `headquarters` string parsing - that was fragile and
// has been replaced by the explicit `states` field.

// Last review date for the curated comparison dataset (price ranges,
// warranty terms, service areas, etc.). Shown as a visible "Last updated"
// cue and emitted as schema.org dateModified so search engines and AI
// answer engines treat the comparison pages as freshly maintained.
// Bump this whenever the builder data below is reviewed/revised.
export const COMPARISON_DATA_UPDATED = '2026-06-18';

export const builderComparisonData = {
  "metricon": {
    slug: "metricon",
    states: ['SA', 'VIC'],
    name: "Metricon",
    logo: "https://static.wixstatic.com/media/eaf73e_3949735b6c074544bffdec3302a0e9da~mv2.webp",
    tagline: "Volume home builder (national)",
    builderType: "Volume Builder",
    priceRange: "$1,900 - $2,800/sqm",
    established: "1976",
    headquarters: "Victoria (National)",
    overview: "One of Australia's largest volume home builders, operating across several states. Metricon's range spans first-home and house-and-land packages through to larger family and upper-tier homes, customised through its Studio M design centre. Ranking and award claims in its marketing are the company's own - verify current standings independently.",
    bestFor: ["First home buyers", "House and land packages", "Standard designs", "Display home variety"],
    designApproach: "Extensive range of pre-designed homes with customisation options through Studio M design centre",
    buildProcess: "6-stage quality assurance process with dedicated site supervisors",
    warranty: "Lifetime Structural Guarantee",
    displayHomes: "Extensive network across Adelaide",
    website: "https://www.metricon.com.au"
  },
  "simonds-homes-sa": {
    slug: "simonds-homes-sa",
    states: ['SA', 'VIC'],
    name: "Simonds Homes",
    logo: "https://static.wixstatic.com/media/eaf73e_e8d3aeec56b1438bb7c5c630bbf02b30~mv2.webp",
    tagline: "Volume home builder (national)",
    builderType: "Volume Builder",
    priceRange: "$1,800 - $2,500/sqm",
    established: "1949",
    headquarters: "Victoria (National)",
    overview: "A national volume builder with a presence in Adelaide's growth corridors, offering competitively priced pre-designed homes and first-home buyer packages.",
    bestFor: ["Budget-conscious buyers", "Northern Adelaide", "First home buyers", "Quick builds"],
    designApproach: "Efficient pre-designed homes focused on value and functionality",
    buildProcess: "Streamlined construction process for efficient delivery",
    warranty: "Structural guarantee",
    displayHomes: "Multiple locations in growth areas",
    website: "https://www.simonds.com.au"
  },
  "fairmont-homes": {
    slug: "fairmont-homes",
    states: ['SA'],
    name: "Fairmont Homes",
    logo: "https://static.wixstatic.com/media/eaf73e_eca99e7d75c640159e13ada49dff36b8~mv2.png",
    tagline: "SA-owned volume / project builder",
    builderType: "Volume/Project Builder",
    priceRange: "$2,000 - $3,000/sqm",
    established: "1959",
    headquarters: "Adelaide (SA owned)",
    overview: "A South Australian-owned builder based in Kent Town, Adelaide, established in 1959. Fairmont builds across metropolitan Adelaide and its growth corridors. Award claims in its marketing are the company's own.",
    bestFor: ["Quality inclusions", "SA families", "Award-winning designs", "Customer service focus"],
    designApproach: "Wide range of designs with quality standard inclusions",
    buildProcess: "Dedicated customer service team throughout build",
    warranty: "Comprehensive structural warranty",
    displayHomes: "Kent Town and growth areas",
    website: "https://fairmonthomes.com.au"
  },
  "australian-building-co": {
    slug: "australian-building-co",
    states: ['SA'],
    name: "ABC Homes",
    logo: "https://static.wixstatic.com/media/eaf73e_78a0128385cb4ee99a6ae92122ceba34~mv2.webp",
    tagline: "Adelaide's Trusted Local Builder",
    builderType: "Volume Builder",
    priceRange: "$1,900 - $2,600/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Established Adelaide builder offering a range of home designs for South Australian families with focus on quality construction.",
    bestFor: ["Local service", "Range of designs", "Adelaide families", "Competitive pricing"],
    designApproach: "Variety of designs suited to Adelaide conditions",
    buildProcess: "Local team with Adelaide expertise",
    warranty: "Structural warranty",
    displayHomes: "Adelaide locations",
    website: "https://www.abchomes.com.au"
  },
  "rossdale-homes": {
    slug: "rossdale-homes",
    states: ['SA'],
    name: "Rossdale Homes",
    logo: "https://static.wixstatic.com/media/eaf73e_9a03c8f5eba04157a0bde0cd71fe7eba~mv2.webp",
    tagline: "Quality Custom Homes in Adelaide",
    builderType: "Project Builder",
    priceRange: "$2,200 - $3,200/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Personalised building experience positioned between volume and full custom builders. Known for flexibility in design modifications.",
    bestFor: ["Design flexibility", "Challenging sites", "Personalised service", "Hills and slopes"],
    designApproach: "Flexible designs with strong modification capabilities",
    buildProcess: "Personal dedicated consultants throughout journey",
    warranty: "Comprehensive warranty",
    displayHomes: "Multiple Adelaide locations",
    website: "https://www.rossdalehomes.com.au"
  },
  "lofty-building-group": {
    slug: "lofty-building-group",
    states: ['SA'],
    name: "Lofty Building Group",
    logo: "https://static.wixstatic.com/media/eaf73e_689a8ec901314d6bac4c4fd0350eafab~mv2.webp",
    tagline: "Building Excellence in Adelaide",
    builderType: "Project Builder",
    priceRange: "$2,100 - $2,900/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Adelaide-based builder offering quality home construction across metropolitan Adelaide with reliable building solutions.",
    bestFor: ["Metropolitan Adelaide", "Quality focus", "Reliable builds", "Project homes"],
    designApproach: "Quality-focused designs for Adelaide conditions",
    buildProcess: "Reliable, structured build process",
    warranty: "Structural warranty",
    displayHomes: "Adelaide metropolitan area",
    website: "https://www.loftybuildinggroup.com.au"
  },
  "alete-homes": {
    slug: "alete-homes",
    states: ['SA'],
    name: "Alete Homes",
    logo: "https://static.wixstatic.com/media/eaf73e_8315640104624e0184cbf5cdb82d7d7a~mv2.webp",
    tagline: "Quality Adelaide Home Builder",
    builderType: "Project Builder",
    priceRange: "$2,000 - $2,800/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Adelaide home builder providing quality new home construction with tailored building solutions across metropolitan Adelaide.",
    bestFor: ["Metropolitan Adelaide", "Tailored solutions", "Quality construction", "Project homes"],
    designApproach: "Tailored designs for Adelaide families",
    buildProcess: "Personalised build approach",
    warranty: "Structural warranty",
    displayHomes: "Adelaide locations",
    website: "https://www.aletehomes.com.au"
  },
  "regent-homes": {
    slug: "regent-homes",
    states: ['SA'],
    name: "Regent Homes",
    logo: "https://regenthomes.com.au/wp-content/uploads/2024/05/regent-logo.png",
    tagline: "Luxury Homes Since 1991",
    builderType: "Custom Builder",
    priceRange: "$3,000 - $5,000+/sqm",
    established: "1991",
    headquarters: "Adelaide",
    overview: "Premier luxury home builder since 1991, five-time winner of SA's Best Display Home Award. Over 2,000 families have trusted Regent.",
    bestFor: ["Luxury homes", "Award-winning designs", "Established reputation", "Premium builds"],
    designApproach: "Envision 3D design technology for visualisation",
    buildProcess: "Premium construction with award-winning quality",
    warranty: "Comprehensive luxury home warranty",
    displayHomes: "Premium display home locations",
    website: "https://www.regenthomes.com.au"
  },
  "format-homes": {
    slug: "format-homes",
    states: ['SA'],
    name: "Format Homes",
    logo: "https://www.formathomes.com.au/wp-content/themes/starter/Format%20Homes%20Logo%20BLACK.svg",
    tagline: "SA's Best Medium Builder 5 Years Running",
    builderType: "Custom Builder",
    priceRange: "$2,200 - $3,200/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Adelaide's trusted custom home builder with over 100 designs. Format pays your rent for up to 36 weeks while they build your dream home.",
    bestFor: ["Custom builds", "Rent assistance program", "Steel frame construction", "Design variety"],
    designApproach: "Over 100 designs with Truecore Steel Frame construction",
    buildProcess: "Up to 36 weeks rent paid while building",
    warranty: "Comprehensive warranty",
    displayHomes: "Angle Vale, Fulham, Oakden, Old Reynella",
    website: "https://www.formathomes.com.au"
  },
  "oakford-homes": {
    slug: "oakford-homes",
    states: ['SA'],
    name: "Oakford Homes",
    logo: "https://customer-assets-rejwkqb3.emergentagent.net/job_eaad4cf4-b013-4778-bbae-d982e539f07c/artifacts/r34gwzv0_Oakford%20Homes%20Logo.png",
    tagline: "Quality Homes Built on Honest Foundations Since 1985",
    builderType: "Custom Builder",
    priceRange: "$2,200 - $3,200/sqm",
    established: "1985 (40+ years, 4,000+ homes)",
    headquarters: "Adelaide",
    overview: "Family-owned Adelaide custom home builder with 40+ years of experience and over 4,000 completed homes. Reputation built on transparent pricing, honest conversations and quality finishes delivered by trades who've been with them for 15, 20, even 25 years. Winner - HIA SA Display Home 2024.",
    bestFor: ["Custom builds", "Knockdown rebuilds", "Adapting proven designs", "Honest transparent pricing"],
    designApproach: "Custom designs built around your life plus a library of proven adaptable designs",
    buildProcess: "Direct contact with experienced professionals from first conversation to handover; no-surprises approach",
    warranty: "Comprehensive Home Builder's warranty",
    displayHomes: "Mount Barker, Oakden, Brooklyn Park, Hawthorn, Encounter Bay - 9 display homes across Adelaide",
    website: "https://www.oakfordhomes.com.au"
  },
  "orizon-homes": {
    slug: "orizon-homes",
    states: ['SA'],
    name: "Orizon Homes",
    logo: "https://lirp.cdn-website.com/53882e27/dms3rep/multi/opt/Screenshot+2025-08-21+153147-1920w.png",
    tagline: "Pre-Sale Renovations & Home Makeovers",
    builderType: "Renovation Specialist",
    priceRange: "$2,000 - $3,500/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Specialists in fast, high-impact pre-sale transformations and home improvements. Delivering quality renovations tailored to your vision.",
    bestFor: ["Pre-sale renovations", "Kitchen & bathroom updates", "Home makeovers", "Quick turnarounds"],
    designApproach: "Transformation-focused design for maximum impact",
    buildProcess: "Dedicated project manager for each renovation",
    warranty: "Renovation warranty",
    displayHomes: "Portfolio of completed projects",
    website: "https://www.orizonhomes.com.au"
  },
  "homecorp-sa": {
    slug: "homecorp-sa",
    states: ['SA'],
    name: "Homecorp",
    logo: `${process.env.REACT_APP_BACKEND_URL}/api/plan-asset?url=https%3A%2F%2Fhomecorp.com%2Fwp-content%2Fuploads%2F2023%2F08%2FHomecorp-Logo-Charcoal-300x54.png`,
    tagline: "Toyota & Panasonic Backed Developer",
    builderType: "Developer/Builder",
    priceRange: "$2,000 - $2,800/sqm",
    established: "Corporate backed",
    headquarters: "Adelaide/Queensland",
    overview: "Trusted residential developer owned by Prime Life Technologies (Toyota and Panasonic). Developing communities across SA and QLD.",
    bestFor: ["Master planned communities", "House and land packages", "Corporate backing", "Community living"],
    designApproach: "Community-focused design approach",
    buildProcess: "Developer-backed build process",
    warranty: "Comprehensive warranty",
    displayHomes: "Within developments",
    website: "https://www.homecorp.com.au"
  },
  "urban-edge-sa": {
    slug: "urban-edge-sa",
    states: ['SA'],
    name: "Urban Edge SA",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/urban-edge-sa.png",
    tagline: "Independent Adelaide custom & NDIS home builder",
    builderType: "Custom & Project Builder",
    established: "Adelaide based",
    headquarters: "Pooraka, Adelaide (SA)",
    overview: "An independent Adelaide home builder offering original, contemporary single and double storey designs that can be fully customised, alongside a catalogue of ready-to-build plans. Urban Edge SA guides clients through the whole journey, from finding land and tailoring a design to finishes at its Pooraka Selections Centre and construction. The company is also a licensed builder of NDIS Specialist Disability Accommodation (SDA) and works across knockdown rebuilds and subdivisions, with a strong base of referred business built on quality and a hands-on approach.",
    bestFor: ["Custom home designs", "NDIS / SDA homes", "Single & double storey homes", "Knockdown rebuilds & subdivisions"],
    designApproach: "Fully customisable designs plus a catalogue of single and double storey plans, finished at the Pooraka Selections Centre",
    buildProcess: "Guided end-to-end process from land and design through selections to construction, delivered with a select network of trade partners",
    warranty: "Statutory SA builder's warranty",
    displayHomes: "Selections Centre, 869 Main North Road, Pooraka",
    website: "https://www.urbanedgesa.com.au"
  },
  "ls-design-and-construct": {
    slug: "ls-design-and-construct",
    states: ['SA'],
    name: "LS Design & Construct",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/ls-design-and-construct.png",
    tagline: "Design & Construction Specialists",
    builderType: "Design & Construct",
    priceRange: "$2,500 - $3,500/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Comprehensive design and construction services combining architectural design with quality building.",
    bestFor: ["Design and construct", "Architectural homes", "Full service", "Project management"],
    designApproach: "In-house architectural design expertise",
    buildProcess: "Full project management from design to completion",
    warranty: "Comprehensive warranty",
    displayHomes: "Portfolio projects",
    website: "https://www.lsdesignandconstruct.com.au"
  },
  "st-construct": {
    slug: "st-construct",
    states: ['SA'],
    name: "ST Construct",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/st-construct.png",
    tagline: "Adelaide Construction Specialists",
    builderType: "Project Builder",
    priceRange: "$2,300 - $3,200/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Professional construction services across Adelaide, specialising in quality residential builds.",
    bestFor: ["Quality residential", "Adelaide metro", "Professional service", "Attention to detail"],
    designApproach: "Quality-focused residential designs",
    buildProcess: "Professional construction management",
    warranty: "Structural warranty",
    displayHomes: "Portfolio examples",
    website: "https://www.stconstruct.com.au"
  },
  "urban3": {
    slug: "urban3",
    states: ['SA'],
    name: "Urban3",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/urban3.png",
    tagline: "Modern Adelaide Homes",
    builderType: "Project Builder",
    priceRange: "$2,400 - $3,300/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Creating modern, contemporary homes across Adelaide with focus on innovative design and quality construction.",
    bestFor: ["Modern designs", "Contemporary style", "Innovative homes", "Quality builds"],
    designApproach: "Modern contemporary design focus",
    buildProcess: "Quality-driven construction",
    warranty: "Structural warranty",
    displayHomes: "Adelaide locations",
    website: "https://www.urban3.com.au"
  },
  "buildscope": {
    slug: "buildscope",
    states: ['SA'],
    name: "BuildScope",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/buildscope.png",
    tagline: "Luxury that lasts",
    builderType: "Custom Builder",
    priceRange: "From $1M (bespoke custom)",
    established: "Family-owned since 2006",
    headquarters: "Adelaide Metro & Adelaide Hills",
    overview: "HIA award-winning, family-owned Adelaide builder creating bespoke luxury homes, high-end renovations, extensions and knockdown rebuilds through considered design, premium craftsmanship and a personal approach. Buildscope takes on projects from $1M upwards and does not build projects below this level.",
    bestFor: ["Bespoke luxury homes", "High-end renovations & extensions", "Sloping and Adelaide Hills sites", "Full custom design"],
    designApproach: "Full custom design flexibility with timber construction and any slope handled",
    buildProcess: "Builder deeply involved from design onwards, with clear client communication throughout",
    warranty: "Structural warranty",
    displayHomes: "Bespoke builds across Adelaide Metro and the Adelaide Hills",
    website: "https://www.buildscope.com.au"
  },
  "hpg-homes": {
    slug: "hpg-homes",
    states: ['SA'],
    name: "HPG",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/hpg.png",
    tagline: "Adelaide Home Builders",
    builderType: "Project Builder",
    priceRange: "$2,100 - $2,900/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Professional home building services across Adelaide with commitment to quality and customer satisfaction.",
    bestFor: ["Professional service", "Quality commitment", "Customer focus", "Adelaide builds"],
    designApproach: "Customer-focused design options",
    buildProcess: "Professional building process",
    warranty: "Structural warranty",
    displayHomes: "Adelaide locations",
    website: "https://www.hpgaustralia.com.au"
  },
  "orne-renovations": {
    slug: "orne-renovations",
    states: ['SA'],
    name: "Orne Renovations",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/orne-renovations.png",
    tagline: "Renovation Specialists Adelaide",
    builderType: "Renovation Specialist",
    priceRange: "$2,500 - $4,000/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Home renovation and extension specialists across Adelaide, transforming existing homes with quality craftsmanship.",
    bestFor: ["Renovations", "Extensions", "Home transformations", "Quality upgrades"],
    designApproach: "Renovation-focused design expertise",
    buildProcess: "Specialist renovation process",
    warranty: "Renovation warranty",
    displayHomes: "Portfolio of renovations",
    website: "https://www.ornerenovations.com.au"
  },
  "la-vida-homes": {
    slug: "la-vida-homes",
    states: ['SA'],
    name: "La Vida Homes",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/la-vida-homes-sa.png",
    tagline: "Adelaide Living Solutions",
    builderType: "Project Builder",
    priceRange: "$2,300 - $3,200/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Quality homes across Adelaide with focus on lifestyle-driven design and construction excellence.",
    bestFor: ["Lifestyle focus", "Quality construction", "Adelaide families", "Design excellence"],
    designApproach: "Lifestyle-driven design approach",
    buildProcess: "Quality-focused construction",
    warranty: "Structural warranty",
    displayHomes: "Adelaide locations",
    website: "https://www.lavitahomes.com.au"
  },
  "ora-projects": {
    slug: "ora-projects",
    states: ['SA'],
    name: "Ora Projects",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/ora-projects.png",
    tagline: "Adelaide Development Specialists",
    builderType: "Developer/Builder",
    priceRange: "$2,200 - $3,000/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Residential developments and new home construction specialists across Adelaide. Formerly known as Double A Development and Construction.",
    bestFor: ["Developments", "New home construction", "Adelaide projects", "Quality builds"],
    designApproach: "Development-focused design",
    buildProcess: "Experienced development process",
    warranty: "Structural warranty",
    displayHomes: "Development sites",
    website: ""
  },
  "ap-designer-homes": {
    slug: "ap-designer-homes",
    states: ['SA'],
    name: "AP Designer Homes",
    logo: "https://ghkultcdfnneodcjmxpn.supabase.co/storage/v1/object/public/builder-logos/ap-designer-homes.png",
    tagline: "Designer Homes Adelaide",
    builderType: "Design Builder",
    priceRange: "$2,800 - $4,000/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide",
    overview: "Architecturally designed homes across Adelaide with focus on premium design and finishes.",
    bestFor: ["Architectural designs", "Premium finishes", "Design-led builds", "Unique homes"],
    designApproach: "Architectural design specialists",
    buildProcess: "Premium construction process",
    warranty: "Comprehensive warranty",
    displayHomes: "By appointment",
    website: "https://www.apdesignerhomes.com.au"
  },
  "build-on": {
    slug: "build-on",
    states: ['SA'],
    name: "Build.On",
    logo: "",
    tagline: "Bespoke Luxury Homes, Renovations & Knockdown Rebuilds",
    builderType: "Custom Builder",
    priceRange: "$2,200 - $3,500/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide (SA)",
    overview: "Adelaide-based licensed builder (BLD 338556) covering Adelaide, the Barossa and the Riverland. Specialists in bespoke luxury homes, major renovations, residential extensions and knockdown rebuild projects with fixed-price quoting.",
    bestFor: ["Custom design", "Luxury homes", "Knockdown rebuild", "Renovations & extensions"],
    designApproach: "Bespoke, end-to-end project management from demolition to completion",
    buildProcess: "Fixed-price quoting consolidating demolition and construction",
    warranty: "Standard structural warranty",
    displayHomes: "By appointment",
    website: "https://build-on.com.au"
  },
  "incredible-homes": {
    slug: "incredible-homes",
    states: ['SA'],
    name: "Incredible Homes / Synergy Housing",
    logo: "",
    tagline: "Bespoke Homes & Specialist Disability Accommodation",
    builderType: "Custom Builder",
    priceRange: "$2,300 - $3,300/sqm",
    established: "Adelaide based",
    headquarters: "Adelaide (SA)",
    overview: "Adelaide-based partnership delivering bespoke residential designs, full home constructions and renovations. Notable specialisation in Specialist Disability Accommodation (SDA) - designing and building compliant, accessible homes for South Australians living with disability.",
    bestFor: ["Custom design", "Specialist Disability Accommodation (SDA)", "Accessible homes", "Renovations"],
    designApproach: "Personalised spatial solutions with accessibility-first options",
    buildProcess: "Full design service prior to construction",
    warranty: "Standard structural warranty",
    displayHomes: "By appointment",
    website: "https://incrediblehomes.com.au"
  },
  "kentbuilt": {
    slug: "kentbuilt",
    states: ['SA'],
    name: "Kentbuilt",
    logo: "",
    tagline: "30+ Years Building Custom SA Homes",
    builderType: "Custom Builder",
    priceRange: "$2,400 - $3,500/sqm",
    established: "30+ years in business",
    headquarters: "Adelaide (SA)",
    overview: "Adelaide-based family-owned builder (BLD 315653) with 30+ years experience. Focuses on custom residential homes, high-end renovations and commercial/industrial projects. Lean structure lets them compete on smaller projects while retaining scale for large custom builds.",
    bestFor: ["Custom design", "High-end renovations", "Commercial", "Established SA operator"],
    designApproach: "Client-first, collaborative from concept through completion",
    buildProcess: "Family-run, small-team supervision on every build",
    warranty: "Structural warranty",
    displayHomes: "By appointment",
    website: "https://kentbuilt.com.au"
  },
  "scott-salisbury-group": {
    slug: "scott-salisbury-group",
    states: ['SA'],
    name: "Scott Salisbury Group",
    logo: "",
    tagline: "SA's Multi-Award-Winning Luxury Custom Home Builder (36+ Years)",
    builderType: "Custom Builder",
    priceRange: "$3,000 - $5,000+/sqm",
    established: "1988 (36+ years)",
    headquarters: "Camden Park, Adelaide (SA)",
    overview: "South Australia's flagship luxury custom home builder with 36+ years of multi-award-winning work. The Group operates four divisions: Scott Salisbury Homes (custom high-end), Scott Salisbury Renovations & Additions, Bradford Homes (attainable luxury) and Bradford Projects (townhouses and apartments).",
    bestFor: ["Luxury custom design", "Large-scale developments", "Renovations & additions", "Townhouses / apartments"],
    designApproach: "Collaborative between client, in-house designers and builders",
    buildProcess: "In-house design and construction across four specialist divisions",
    warranty: "Comprehensive warranty",
    displayHomes: "Flagship display home, open seven days",
    website: "https://scottsalisburygroup.com.au"
  },
  "burra-built": {
    slug: "burra-built",
    states: ['VIC'],
    name: "Burra Built",
    logo: "/images/business-logos/burra-built.png",
    tagline: "Built With Quality, Built to Last",
    builderType: "Custom Builder",
    priceRange: "$250K - $2.5M+",
    established: "5-10 years in business",
    headquarters: "Victoria",
    overview: "Family-owned Victorian custom builder covering Melbourne Metro, Mornington Peninsula, Yarra Valley and regional VIC. New homes, renovations, extensions, granny flats and knockdown rebuilds.",
    bestFor: ["Custom design", "Family homes", "First home buyers", "Knockdown rebuild", "Renovations & extensions"],
    designApproach: "Full custom design or client-supplied plans, no fixed catalogue",
    buildProcess: "Small team, hands-on from first concept through to handover",
    warranty: "Standard structural warranty",
    displayHomes: "By appointment",
    website: "https://www.burrabuilt.com.au"
  }
};

// Helper to get all builder slugs
export const getBuilderSlugs = () => Object.keys(builderComparisonData);

// Helper to get builder by slug
export const getBuilderData = (slug) => builderComparisonData[slug] || null;

// ---------------------------------------------------------------------------
// State-aware helpers
// ---------------------------------------------------------------------------
// SA-facing surfaces (Compare Hub, Builders Hub, Most Compared strip, related
// links, sitemaps) MUST call getBuildersByState('SA') so VIC-only partners
// (e.g. Burra Built) don't leak into an Adelaide user's UI. Symmetric for VIC.
// A national partner can carry multiple states (e.g. ['SA','VIC']) and will
// appear in both contexts.
export const getBuildersByState = (stateCode) => {
  if (!stateCode) return Object.entries(builderComparisonData).map(([slug, b]) => ({ slug, ...b }));
  const code = String(stateCode).toUpperCase();
  return Object.entries(builderComparisonData)
    .filter(([, b]) => Array.isArray(b.states) && b.states.includes(code))
    .map(([slug, b]) => ({ slug, ...b }));
};

export const getBuilderSlugsByState = (stateCode) =>
  getBuildersByState(stateCode).map((b) => b.slug);

// True when the builder is available in the given state.
export const builderIsInState = (slug, stateCode) => {
  const b = builderComparisonData[slug];
  if (!b || !Array.isArray(b.states)) return false;
  return b.states.includes(String(stateCode).toUpperCase());
};

// Generate all valid comparison combinations. When `stateCode` is
// provided, only builders available in that state are paired - this
// prevents SA users seeing "Metricon vs Burra Built" style comparisons
// where one builder is irrelevant to their market.
export const getAllComparisons = (stateCode = null) => {
  const slugs = stateCode ? getBuilderSlugsByState(stateCode) : getBuilderSlugs();
  const comparisons = [];

  for (let i = 0; i < slugs.length; i++) {
    for (let j = i + 1; j < slugs.length; j++) {
      comparisons.push({
        builder1: slugs[i],
        builder2: slugs[j],
        slug: `${slugs[i]}-vs-${slugs[j]}`
      });
    }
  }

  return comparisons;
};

// Get comparison data for two builders
export const getComparisonData = (slug1, slug2) => {
  const builder1 = getBuilderData(slug1);
  const builder2 = getBuilderData(slug2);
  
  if (!builder1 || !builder2) return null;
  
  return { builder1, builder2 };
};
